import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconCheck } from '@tabler/icons-react';
import {
  STOREFRONT_LEAD_MAGNET_SLUGS,
  STOREFRONT_LEAD_MAGNETS,
  type StorefrontLeadMagnetStatusItem,
  type StorefrontLeadMagnetsStatus,
} from '@wapharma/shared';
import { Button, Group, Stack, Text } from '@wapharma/design-system';
import { apiGet, apiPost } from '../api';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { useToast } from '../contexts/ToastContext';
import { useStaffRoles } from '../hooks/useStaffRoles';
import { homePreviewUrl } from '../lib/storefrontPublicUrl';
import styles from './SetupWizard.module.css';

function statusLabel(item: StorefrontLeadMagnetStatusItem): string {
  if (item.status === 'ready') return 'Prêt';
  if (item.status === 'blocked') return 'Publié — matching vide';
  if (item.status === 'draft') return 'Brouillon';
  if (item.flowId) return 'Archivé';
  return 'Absent';
}

function statusHint(item: StorefrontLeadMagnetStatusItem): string | null {
  if (item.status === 'blocked') {
    return (
      item.blockReason ??
      'Aucun produit tagué routine:* — le matching produit restera vide sur /diagnostic.'
    );
  }
  if (item.status === 'draft') {
    return 'Existe mais non publié — les clients ne peuvent pas l’ouvrir.';
  }
  if (item.status === 'missing') {
    return item.flowId
      ? 'Archivé — restauration requise pour le storefront.'
      : 'Absent — le storefront masque le CTA associé.';
  }
  return null;
}

type LaunchFlowsStepProps = {
  onChanged?: () => void;
};

/** Launch step: assured storefront lead magnets (routine quiz + photo). */
export function LaunchFlowsStep({ onChanged }: LaunchFlowsStepProps) {
  const [status, setStatus] = useState<StorefrontLeadMagnetsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { canMutateLeadFlows } = useStaffRoles();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<{ data: StorefrontLeadMagnetsStatus }>(
        '/lead-flows/storefront-status',
        getToken() ?? undefined,
      );
      setStatus(res.data ?? null);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const ensureMagnet = async (slug: string, restored = false) => {
    setBusySlug(slug);
    try {
      const res = await apiPost<{ data: { id: string } }>(
        `/lead-flows/ensure-storefront/${slug}`,
        { publish: true },
        getToken() ?? undefined,
      );
      showToast(
        restored
          ? 'Flow restauré — visible sur le storefront'
          : 'Flow créé et publié — visible sur le storefront',
        { tone: 'success' },
      );
      await load();
      onChanged?.();
      if (res.data?.id) navigate(`/crm/flows/${res.data.id}`);
    } catch (e: unknown) {
      showToast(getDisplayError(e), { tone: 'error' });
    } finally {
      setBusySlug(null);
    }
  };

  const items = STOREFRONT_LEAD_MAGNET_SLUGS.map((slug) => {
    const live = status?.[slug];
    const def = STOREFRONT_LEAD_MAGNETS.find((m) => m.slug === slug);
    return {
      slug,
      def,
      item: live,
    };
  });

  return (
    <Stack gap="md">
      <Text size="xs" c="dimmed">
        Publiés au démarrage API — questionnaire routine et analyse photo (`/diagnostic`). Vérifiez
        qu’ils sont ouverts avant le lancement.
      </Text>

      {loading && !status ? (
        <Text size="sm" c="dimmed">
          Chargement…
        </Text>
      ) : null}

      <div className={styles.roleTagGrid} role="list" aria-label="Parcours vitrine assurés">
        {items.map(({ slug, def, item }) => {
          const name = item?.label ?? def?.defaultName ?? slug;
          const ok = item?.status === 'ready' || item?.status === 'blocked';
          const hint = item ? statusHint(item) : null;
          const busy = busySlug === slug;
          return (
            <div
              key={slug}
              role="listitem"
              className={`${styles.roleTag} ${ok ? styles.roleTagPresent : styles.roleTagPending}`}
              style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}
            >
              <Group gap="xs" justify="space-between" wrap="nowrap">
                <span className={styles.roleTagLabel}>{name}</span>
                {ok ? (
                  <span
                    className={styles.roleTagCheck}
                    aria-label={`${name} — ${statusLabel(item!)}`}
                    title={statusLabel(item!)}
                  >
                    <IconCheck size={14} stroke={2.2} aria-hidden />
                  </span>
                ) : null}
              </Group>
              <Text size="xs" c="dimmed">
                {item ? statusLabel(item) : 'Statut inconnu'}
                {def?.storefrontPath ? ` · ${def.storefrontPath}` : null}
              </Text>
              {hint ? (
                <Text size="xs" c="dimmed">
                  {hint}
                </Text>
              ) : null}
              <Group gap="xs" wrap="wrap">
                {item && (item.status === 'missing' || item.status === 'draft') ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="soft"
                    disabled={busy || !canMutateLeadFlows}
                    onClick={() => {
                      if (item.status === 'draft' && item.flowId) {
                        navigate(`/crm/flows/${item.flowId}`);
                        return;
                      }
                      void ensureMagnet(slug, item.status === 'missing' && Boolean(item.flowId));
                    }}
                  >
                    {busy
                      ? '…'
                      : item.status === 'draft'
                        ? 'Ouvrir le flow'
                        : item.flowId
                          ? 'Restaurer'
                          : 'Créer et publier'}
                  </Button>
                ) : null}
                {item?.status === 'blocked' ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate('/catalog/taxonomy?taxonomyTab=tags')}
                  >
                    Tags catalogue
                  </Button>
                ) : null}
                {item?.flowId && item.status !== 'missing' ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/crm/flows/${item.flowId}`)}
                  >
                    CRM
                  </Button>
                ) : null}
                {def?.storefrontPath ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      window.open(homePreviewUrl(def.storefrontPath), '_blank', 'noopener,noreferrer')
                    }
                  >
                    Aperçu
                  </Button>
                ) : null}
              </Group>
            </div>
          );
        })}
      </div>
    </Stack>
  );
}
